import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";

const day = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

/** Preliminary opportunity submissions: read one, move it through the stages. */
export default function Applications() {
  const [data, setData] = useState(null);
  const [box, setBox] = useState(null);
  const [chase, setChase] = useState(null);
  const [open, setOpen] = useState("");
  const [notify, setNotify] = useState(true);

  const load = () => {
    api.get("/admin/applications").then((r) => setData(r.data)).catch(() => setData(null));
    api.get("/admin/email-outbox").then((r) => setBox(r.data)).catch(() => setBox(null));
    api.get("/admin/applications/reminders").then((r) => setChase(r.data)).catch(() => setChase(null));
  };

  useEffect(() => {
    load();
  }, []);

  const move = async (reference, status) => {
    try {
      const { data: res } = await api.post(`/admin/applications/${reference}/status`, { status, notify });
      toast.success(`${reference} → ${res.stage}${notify ? (res.applicant_told ? " · applicant emailed" : " · email queued") : ""}`);
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const flush = async () => {
    try {
      const { data: res } = await api.post("/admin/email-outbox/flush");
      toast.success(
        res.tried === 0
          ? "Nothing waiting to send"
          : `Retried ${res.tried} — ${res.sent} sent, ${res.failed} still waiting`
      );
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const nudge = async () => {
    try {
      const { data: res } = await api.post("/admin/applications/reminders/run");
      toast.success(
        res.considered === 0
          ? "Nobody is due a nudge"
          : `${res.considered} chased — ${res.sent} sent, ${res.queued} queued`
      );
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const requeue = async () => {
    try {
      const { data: res } = await api.post("/admin/email-outbox/requeue");
      toast.success(res.requeued ? `${res.requeued} back in the queue` : "Nothing exhausted");
      load();
    } catch (err) {
      toast.error(errText(err));
    }
  };

  return (
    <div data-testid="admin-applications-page">
      <p className="overline">Inbox</p>
      <h1 className="display-2 mt-4">Preliminary Submissions</h1>

      {data ? (
        <>
          <div className="mt-8 flex flex-wrap items-center gap-3" data-testid="applications-email-controls">
            <label className="flex cursor-pointer items-center gap-2 border border-zinc-800 px-3 py-1.5">
              <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)}
                data-testid="applications-notify-toggle" className="h-3.5 w-3.5 accent-ochre" />
              <span className="mono text-[0.55rem] uppercase tracking-[0.14em] text-zinc-300">
                Email the applicant on every stage change
              </span>
            </label>
            {box ? (
              <span className="mono border border-zinc-800 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-400" data-testid="applications-outbox-stats">
                Email queue: <span className="text-ochre">{box.pending}</span> waiting ·{" "}
                <span className={box.failed ? "text-maasai" : "text-zinc-500"}>{box.failed}</span> exhausted ·{" "}
                {box.sent} sent
                {box.next_retry_at ? ` · next retry ${day(box.next_retry_at)}` : ""}
              </span>
            ) : null}
            {chase ? (
              <span className="mono border border-zinc-800 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-400" data-testid="applications-chase-stats">
                Awaiting an answer: <span className={chase.stalled ? "text-maasai" : "text-zinc-500"}>{chase.stalled}</span> ·{" "}
                {chase.chaseable} chaseable · {chase.exhausted} chased out
              </span>
            ) : null}
            <button onClick={nudge} data-testid="applications-run-reminders"
              className="mono border border-zinc-600 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-300 hover:border-ochre hover:text-ochre">
              Chase an unanswered question
            </button>
            <button onClick={flush} data-testid="applications-flush-outbox"
              className="mono border border-ochre px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-ochre hover:bg-ochre hover:text-black">
              Retry queued email now
            </button>
            {box?.failed ? (
              <button onClick={requeue} data-testid="applications-requeue-outbox"
                className="mono border border-maasai px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-maasai hover:bg-maasai hover:text-white">
                Requeue {box.failed} exhausted
              </button>
            ) : null}
          </div>

          <div className="mt-6 flex flex-wrap gap-2" data-testid="applications-counts">
            {data.stages.map((s) => (
              <span key={s.id} className="mono border border-zinc-800 px-3 py-1.5 text-[0.55rem] uppercase tracking-[0.14em] text-zinc-400">
                {s.label} <span className="text-ochre">{data.by_status[s.id] || 0}</span>
              </span>
            ))}
          </div>

          <div className="mt-8 space-y-3" data-testid="applications-list">
            {data.rows.length === 0 ? (
              <p className="body-copy">Nobody has told us about an opportunity yet.</p>
            ) : (
              data.rows.map((a) => (
                <div key={a.reference} className="border border-zinc-900 bg-ink-surface p-5" data-testid={`application-${a.reference}`}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <p className="font-display text-base uppercase tracking-wide text-white">
                        {a.payload.full_name} — {a.payload.county}
                      </p>
                      <p className="mt-1 text-xs text-zinc-500">
                        {a.reference} · {a.payload.opportunity_category || "—"} ·{" "}
                        {a.payload.organisation || "no organisation"} · told us {day(a.created_at)}
                      </p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="mono border border-ochre px-3 py-1 text-[0.52rem] uppercase tracking-[0.14em] text-ochre">
                        {data.stages.find((s) => s.id === a.status)?.label || a.status}
                      </span>
                      <button onClick={() => setOpen(open === a.reference ? "" : a.reference)}
                        data-testid={`application-open-${a.reference}`}
                        className="mono border border-zinc-700 px-3 py-1 text-[0.52rem] uppercase tracking-[0.14em] text-zinc-300">
                        {open === a.reference ? "Hide" : "Read"}
                      </button>
                    </div>
                  </div>

                  {open === a.reference ? (
                    <div className="mt-5 space-y-4 border-t border-zinc-900 pt-5" data-testid={`application-detail-${a.reference}`}>
                      <p className="text-sm leading-relaxed text-zinc-300">{a.payload.description}</p>
                      <div className="grid gap-3 text-xs text-zinc-400 sm:grid-cols-2 lg:grid-cols-3">
                        {[
                          ["Telephone", a.payload.mobile],
                          ["Email", a.payload.email],
                          ["Organisation", a.payload.organisation || "—"],
                          ["Standing", a.payload.authority || "—"],
                          ["Consent to contact", a.payload.consent_contact ? "Yes" : "No"],
                          ["Understands they appoint any professional directly", a.payload.appoint_own_professional ? "Yes" : "No"],
                          ["Consent to updates", a.payload.consent_updates ? "Yes" : "No"],
                          ["Consent to marketing", a.payload.consent_marketing ? "Yes" : "No"],
                        ].map(([k, v]) => (
                          <p key={k}><span className="text-zinc-600">{k}:</span> {v}</p>
                        ))}
                      </div>
                      <p className="border-l-2 border-maasai bg-[#0b0b0b] p-4 text-xs leading-relaxed text-zinc-300"
                        data-testid={`application-no-share-${a.reference}`}>
                        This submission is not to be forwarded to anyone — not an estate agent, a valuer, an
                        advocate or a funder. If regulated work is needed, tell them to appoint a licensed
                        professional themselves and point them at the published register.
                      </p>
                      <div className="flex flex-wrap gap-2 pt-2">
                        {data.stages.map((s) => (
                          <button key={s.id} onClick={() => move(a.reference, s.id)}
                            disabled={s.id === a.status}
                            data-testid={`application-stage-${a.reference}-${s.id}`}
                            className="mono border border-zinc-700 px-3 py-1.5 text-[0.52rem] uppercase tracking-[0.14em] text-zinc-300 transition-colors hover:border-ochre hover:text-ochre disabled:opacity-40">
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ) : null}
                </div>
              ))
            )}
          </div>
        </>
      ) : (
        <p className="body-copy mt-8">Loading applications…</p>
      )}
    </div>
  );
}
